import React, { useContext, useEffect, useMemo, useState } from "react";
import "./style.css";
import { ReactTable } from "../../Components/Molecules";
import { Breadcrumb, CustomPopUpModal, SuccessPopupModal } from "../../Components/Atoms";
import { AddModelIcon, DeleteIcon, EditModelIcon, ManagePermissionIcon, } from "../../Assets/images/svgs";
import { Button, Spinner, Tooltip, useToast } from "@chakra-ui/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SUB_USERS_HEADERS } from "../../Library/tableHeaders";
import PATHS from "../../Routes/paths";
import { deleteSubUser, getUserAndVehicleDetails } from "../../APIs";
import { StoreContext } from "../../Library/contexts";
import ManageAccessCodes from "./ManageFeaturePermission";

const SubUsers = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const Toast = useToast();
  const [customPopupContent, setCustomPopupContent] = useState({});
  const [successPopUpContent, setSuccessPopUpContent] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [subUsers, setSubUsers] = useState([]);
  const { allSubUsers, setAllSubUsers, userVehicles, setUserVehicles } = useContext(StoreContext);

  const handleFetchSubUsers = () => {
    setIsLoading(true);
    getUserAndVehicleDetails({}, (err, data = []) => {
      if (err) console.error("Error fetching sub users");
      else {
        const updatedData = data.map((user) => ({
          ...user,
          nm: `${user.fn || ""} ${user.mn || ""} ${user.ln || ""}`.trim(),
        }));
        setSubUsers(updatedData);
        setAllSubUsers(updatedData);
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (searchParams.get("fetchSubUsers")) {
      handleFetchSubUsers();
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("fetchSubUsers");
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
    else if (allSubUsers && allSubUsers.length) setSubUsers(allSubUsers);
    else handleFetchSubUsers();
  }, [allSubUsers]);

  const getSubUserStatusLabel = (status) => (status ? "Active" : "Inactive");

  const handleManageFeaturePerm = (userDets) => {
    const handlePopup = (msg) => setSuccessPopUpContent({
      open: true,
      modalMsg: msg,
      closeBtn: "Okay",
      modalSize: "lg",
      onModalClose: () => setSuccessPopUpContent({}),
    });
    setCustomPopupContent({
      open: true,
      modalTitle: "Manage Feature Permission",
      modalBody: (
        <ManageAccessCodes
          userDets={userDets}
          onClose={() => setCustomPopupContent({})}
          onSuccess={(msg) => handlePopup( typeof msg === "string" ? msg : "Feature permission for subuser updated successfully")}
          onFail={() => handlePopup("Error updating feature permission for subuser")}
        />
      ),
      onModalClose: () => setCustomPopupContent({}),
    });
  };

  const handleDeleteSubUser = (uid, subusers) => {
    setCustomPopupContent({
      open: true,
      modalBody: (
        <div>
          <span className="subuser-del-msg">Deleting subuser will unassign the vehicles. Are you sure you want to delete subuser?</span>
        </div>
      ),
      closeBtn: "Close",
      onModalClose: () => setCustomPopupContent({}),
      confirmBtn: "Delete",
      onConfirm: () => {
        deleteSubUser(+uid, (err) => {
          if (err) {
            console.error("Error deleting subuser");
            Toast({ isClosable: true, description: "Error deleting subuser, please try later !!", position: "bottom", variant: "subtle", duration: 3000 })
          }
          else {
            let filteredData = subusers.filter((subUser) => subUser.idx !== +uid);
            setSubUsers(filteredData);
            setAllSubUsers(filteredData);

            let updatedUserVehicles = userVehicles.map((veh) => ({
              ...veh,
              subUsers: veh.subUsers ? veh.subUsers.filter((subUserId) => subUserId !== +uid) : [],
            }));
            setUserVehicles(updatedUserVehicles);

            setCustomPopupContent({});
            setSuccessPopUpContent({
              open: true,
              modalMsg: "Subuser deleted successfully",
              closeBtn: "Okay",
              modalSize: "lg",
              onModalClose: () => setSuccessPopUpContent({}),
            })
          }
        })
      },
    });
  }

  const columns = useMemo(
    () =>
      Object.values(SUB_USERS_HEADERS).map((header) => {
        return {
          Header: header.nm,
          accessor: header.key,
          disableSortBy: !header.sorting,
          Cell: (row) => {
            if (row.column.id === "vehCnt") return <span className="">{row.value}</span>;

            if (row.column.id === "isa") {
              return (
                <span
                  className={`sub-user-status ${
                    row.value ? "su-active" : "su-inactive"
                  }`}
                >
                  {getSubUserStatusLabel(row.value)}
                </span>
              );
            }

            if (row.column.id === "nm") {
              return <Link className="subuser-name" to={`${PATHS.SUB_USERS}/${PATHS.SUB_USER_DETS}?mob=${row.row.original.mob}`}>{row.value || "-"}</Link>
            }

            if (row.column.id === "Options") {
              return (
                <div className="subUser-options-cnt">
                  <Tooltip label="Edit">
                    <EditModelIcon onClick={() => { navigate( `${PATHS.SUB_USERS}/${PATHS.CREATE_SUB_USER}?edit=true&mob=${row.row.original.mob}&fromSubUsers=1` ); }} />
                  </Tooltip>
                  <Tooltip label="Manage feature permissions">
                    <ManagePermissionIcon onClick={() => handleManageFeaturePerm(row.row.original) } />
                  </Tooltip>
                  <Tooltip label="Delete subuser">
                    <DeleteIcon onClick={() => handleDeleteSubUser(row.row.original.idx, allSubUsers)} />
                  </Tooltip>
                </div>
              );
            }
            return <span className="">{row.value || "-"}</span>;
          },
        };
      }),
    [allSubUsers]
  );

  const breadcrumbPaths = [{ idx: 1, name: "Subusers", cb: () => {} }];

  return (
    <div className="panel-main sub-user">
      <div className="bc-cnt">
        <Breadcrumb paths={breadcrumbPaths} />
      </div>
      <div className="btn-cnt">
        <Button
          leftIcon={<AddModelIcon />}
          className="create-subuser"
          onClick={() =>
            navigate(`${PATHS.SUB_USERS}/${PATHS.CREATE_SUB_USER}`)
          }
        >
          Create new subuser
        </Button>
      </div>
      {isLoading ? (
        <div className="loader-cnt">
          <Spinner size={"lg"}/>
        </div>
      ) : (
        <ReactTable
          columns={columns}
          data={subUsers}
          showSearch={true}
          pageSize={10}
        />
      )}
      <CustomPopUpModal {...customPopupContent} />
      <SuccessPopupModal {...successPopUpContent} />
    </div>
  );
};

export default SubUsers;
